import React, { useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { COLORS } from '@clutter/clean';

import { LocationLinksDocument } from '@graphql/platform';

import { BASE_JSON_LD_BREADCRUMB_LIST } from '@root/components/locations/shared/structured_data';
import { BusinessStats } from '@root/components/www/home/business_stats';

import { Footer } from '@shared/content/footer';
import { HowItWorks } from '@shared/how_it_works';
import { NavBar } from '@shared/nav_bar';

import { CityLinks } from './shared/city_links';
import { LocationsHero } from './shared/locations_hero';
import { Section } from './shared/section';
import { ServiceAreaInquiry } from './shared/service_area_inquiry';
import { ValueProposition } from './shared/value_proposition';

const PageContainer = styled.div`
  background: ${COLORS.cloud};
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding-top: 72px; /* reserve space for fixed navbar */
`;

export const Locations = () => {
  const { data } = useQuery(LocationLinksDocument);
  const states = data?.states ?? [];
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 10);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const descriptionContent =
    'Clutter offers storage and moving services across the US. ' +
    'Find a storage location near you to get affordable rates, secure storage, and convenient services.';

  return (
    <>
      <HelmetProvider context={global.helmetContext}>
        <Helmet>
          <title>
            Find Storage Units | Pickup & Delivery Storage | Clutter
          </title>
          <meta name="description" content={descriptionContent}></meta>
          <script type="application/ld+json">
            {JSON.stringify(BASE_JSON_LD_BREADCRUMB_LIST)}
          </script>
        </Helmet>
      </HelmetProvider>
      <NavBar scrolled={scrolled} />
      <PageContainer>
        <LocationsHero />
        <ValueProposition />
        <HowItWorks
          contentPadding={['48px 24px', '60px 24px', '94px 156px']}
          alignStart
        />
        <BusinessStats
          background={COLORS.tealPrimary}
          textColor={COLORS.cloud}
          descriptionFontSize="18px"
          maxWidth="1280px"
        />
        <ServiceAreaInquiry states={states} />
        <Section padding="0px 0 96px">
          <CityLinks states={states} service="storage" />
        </Section>
      </PageContainer>
      <Footer />
    </>
  );
};
